import { useState } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import PaginationControlsWithQuery from '~/components/PaginationControls';
import { useDepots } from '~/hooks/api/useDepots';
import messages from './messages';
import styles from './styles.module.css';
import ListItemButton from '@mui/material/ListItemButton';

const route = getRouteApi('/depots/');

export const DepotList = () => {
  const navigate = useNavigate();
  const search = route.useSearch();

  const getInitialPage = () => {
    return search.page ?? 0;
  };

  const [page, setPage] = useState(getInitialPage());
  const [perPage, setPerPage] = useState(20);

  const { data: depotsResponse, isLoading } = useDepots({
    page, // Hook handles 0-based to 1-based conversion
    perPage,
  });

  const depots = depotsResponse?.data ?? [];
  const totalCount = depotsResponse?.totalCount ?? 0;

  const createDepot = () => {
    navigate({ to: '/depots/new' });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    navigate({ to: '/depots', search: { page: newPage } });
  };

  return (
    <ContentView
      header={
        <div>
          <AppBar position="static">
            <Toolbar>
              <DrawerButton />
              <Typography
                variant="h6"
                sx={{
                  color: 'inherit',
                  ml: 2,
                }}
              >
                <FormattedMessage {...messages.title} />
              </Typography>
            </Toolbar>
          </AppBar>
        </div>
      }
      isLoading={isLoading}
      floatingActionButton={
        <Fab onClick={createDepot} color="secondary">
          <AddIcon data-testid="create-depot" />
        </Fab>
      }
    >
      {!isLoading && depots.length === 0 && (
        <ListItem data-test="empty-list-item">
          <Typography color="textSecondary">
            <FormattedMessage {...messages.noDepotsYet} />
          </Typography>
        </ListItem>
      )}
      {!isLoading && (
        <>
          <PaginationControlsWithQuery
            totalCount={totalCount}
            isLoading={isLoading}
            initialPage={page}
            perPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={setPerPage}
            controlsClassName={styles['pagination-controls']}
          />
          <List>
            {depots.map((depot) => (
              <ListItem key={depot.id} disablePadding>
                <ListItemButton component={Link} to={`/depots/${depot.id}`}>
                  <ListItemText
                    primary={depot.name}
                    secondary={`${depot.address}, ${depot.zip} ${depot.city}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </ContentView>
  );
};

export default DepotList;
